// Betrieb › Radio importieren (h-umzug).
//
// Ablauf: Archiv wählen → hochladen (mit Fortschritt) → der Server prüft
// Manifest und Prüfsummen, legt das Radio in einer Transaktion an und setzt
// die Dateien ein. Ist das Kürzel hier schon vergeben, fragt die Seite nach
// einem anderen und setzt fort, ohne neu hochzuladen. Am Ende ist das Radio
// gesperrt – damit nicht zwei Server gleichzeitig senden.

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { umzugApi, type UploadFortschritt } from "../../api";
import { useAuth } from "../../auth/AuthProvider";
import { errorText } from "../../components/streaming-ui";
import { Meldungen, Sym } from "../../components/social-ui";
import { Button, Callout, Card, Page, PageHeader } from "../../components/ui";
import { bytesText, datumText, zahlText } from "../../components/hosting-ui";
import { AuftragStatus, Balken, fortschrittText, laeuftNoch, useAuftraege } from "../../components/umzug-ui";
import type { UmzugAuftrag, UmzugImportErgebnis } from "../../types";
import { NurBetreiber } from "./NurBetreiber";

export function ImportPage(): JSX.Element {
  const { user } = useAuth();
  if (user?.role !== "super_admin") return <NurBetreiber titel="Radio importieren" />;
  return <ImportInhalt />;
}

function ImportInhalt(): JSX.Element {
  const [datei, setDatei] = useState<File | null>(null);
  const [slug, setSlug] = useState("");
  const [upload, setUpload] = useState<UploadFortschritt | null>(null);
  const [aktuellId, setAktuellId] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const eingabe = useRef<HTMLInputElement>(null);
  const { liste, neu } = useAuftraege(() => umzugApi.importe().then((r) => r.importe));
  const aktuell = liste?.find((a) => a.id === aktuellId) ?? null;

  const hochladen = async (): Promise<void> => {
    if (!datei) return;
    setFehler(null);
    setUpload({ geladen: 0, gesamt: datei.size });
    try {
      const r = await umzugApi.importHochladen(datei, slug.trim() || null, setUpload);
      setAktuellId(r.auftrag.id);
      neu();
    } catch (err) {
      setFehler(errorText(err));
    } finally {
      setUpload(null);
    }
  };

  const neuerVersuch = (): void => {
    setAktuellId(null);
    setDatei(null);
    setSlug("");
    if (eingabe.current) eingabe.current.value = "";
  };

  return (
    <Page className="max-w-4xl">
      <Link to="/betrieb/radios" className="btn btn-ghost -ml-2 mb-2">
        <Sym name="back" /> Alle Radios
      </Link>
      <PageHeader
        eyebrow="Betrieb"
        title="Radio importieren"
        description="Ein Radio aus einem Umzugsarchiv (Export einer webradiosuite) auf diesem Server anlegen – mit Team, Sendeplänen, Mediathek und Bildern."
      />
      <Meldungen error={fehler} />

      {!aktuell && (
        <Card title="Archiv wählen" className="mb-5">
          <div className="grid gap-4 sm:grid-cols-[1fr_minmax(0,14rem)]">
            <label className="block min-w-0">
              <span className="field-label">Umzugsarchiv (.tar.gz)</span>
              <input
                ref={eingabe}
                type="file"
                accept=".gz,.tgz,application/gzip"
                className="input file:mr-3 file:rounded-md file:border-0 file:bg-input file:px-3 file:py-1 file:text-sm"
                onChange={(e) => setDatei(e.target.files?.[0] ?? null)}
                disabled={!!upload}
              />
              {datei && <span className="field-hint block">{datei.name} · {bytesText(datei.size)}</span>}
            </label>
            <label className="block">
              <span className="field-label">Kürzel (optional)</span>
              <input className="input font-mono" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="wie im Archiv" disabled={!!upload} spellCheck={false} autoComplete="off" />
              <span className="field-hint block">Leer = das Kürzel aus dem Archiv.</span>
            </label>
          </div>
          {upload && (
            <div className="mt-4">
              <Balken anteil={upload.gesamt ? upload.geladen / upload.gesamt : 0} label={`Hochgeladen: ${bytesText(upload.geladen)} von ${bytesText(upload.gesamt)}`} />
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button variant="primary" disabled={!datei || !!upload} onClick={() => void hochladen()}>
              <Sym name="up" /> {upload ? "Wird hochgeladen …" : "Hochladen und importieren"}
            </Button>
            <span className="text-xs text-muted">Das Radio ist danach gesperrt – prüfen, dann entsperren.</span>
          </div>
          <ul className="mt-5 space-y-1.5 text-sm text-secondary">
            <li className="flex gap-2"><Sym name="shield" className="mt-0.5 h-4 w-4 text-muted" />Jede Datei wird gegen das Manifest geprüft (SHA-256); Archive einer neueren Suite werden abgelehnt.</li>
            <li className="flex gap-2"><Sym name="key" className="mt-0.5 h-4 w-4 text-muted" />Kennungen bleiben erhalten. Gibt es eine schon auf diesem Server, bricht der Import mit einer Liste ab.</li>
            <li className="flex gap-2"><Sym name="users" className="mt-0.5 h-4 w-4 text-muted" />E-Mail-Adressen, die ein anderes Radio hier schon nutzt, verhindern den Import (Liste im Ergebnis).</li>
          </ul>
        </Card>
      )}

      {aktuell && <AuftragAnsicht a={aktuell} onFortsetzen={neu} onNeu={neuerVersuch} onFehler={setFehler} />}

      <Card title="Bisherige Importe" padded={false}>
        {liste === null ? (
          <p className="card-body text-sm text-muted">Wird geladen …</p>
        ) : liste.length === 0 ? (
          <p className="card-body text-sm text-muted">Noch kein Import.</p>
        ) : (
          <ul className="divide-y divide-default">
            {liste.map((a) => (
              <li key={a.id}>
                <button type="button" onClick={() => setAktuellId(a.id)} className="flex w-full flex-col gap-1 px-4 py-3 text-left hover:bg-input/60 sm:flex-row sm:items-center sm:gap-4">
                  <span className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    <AuftragStatus a={a} />
                    <span className="truncate text-sm font-medium text-primary">{a.radio_name ?? a.optionen?.dateiname ?? "Archiv"}</span>
                    {a.radio_slug && <code className="font-mono text-xs text-muted">{a.radio_slug}</code>}
                  </span>
                  <span className="text-xs text-muted">{datumText(a.created_at, true)} · {bytesText(a.bytes)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Page>
  );
}

const SCHRITTE = [
  { key: "hochladen", label: "Hochgeladen" },
  { key: "pruefen", label: "Archiv geprüft" },
  { key: "vorpruefung", label: "Kürzel, Kennungen, E-Mails geprüft" },
  { key: "datenbank", label: "Datenbank angelegt" },
  { key: "dateien", label: "Dateien eingesetzt" },
];

function schrittIndex(a: UmzugAuftrag): number {
  if (a.status === "fertig") return SCHRITTE.length;
  // Abgebrochen: Der Grund sagt, bei welchem Schritt.
  if (a.status === "kuerzel_noetig") return 2;
  if (a.status === "fehler") {
    const g = a.fehler_details?.grund;
    if (g === "ungueltig" || g === "format" || g === "zu_neu" || g === "art") return 1;
    if (g === "kollision" || g === "email_kollision") return 2;
    return 3;
  }
  const i = SCHRITTE.findIndex((s) => s.key === a.fortschritt?.schritt);
  return i < 0 ? 1 : i;
}

function AuftragAnsicht({ a, onFortsetzen, onNeu, onFehler }: {
  a: UmzugAuftrag;
  onFortsetzen: () => void;
  onNeu: () => void;
  onFehler: (t: string | null) => void;
}): JSX.Element {
  const [slug, setSlug] = useState("");
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => { setSlug(""); }, [a.id]);

  const fortsetzen = async (): Promise<void> => {
    setLaeuft(true);
    onFehler(null);
    try {
      await umzugApi.importFortsetzen(a.id, slug.trim());
      onFortsetzen();
    } catch (err) {
      onFehler(errorText(err));
    } finally {
      setLaeuft(false);
    }
  };
  const verwerfen = async (): Promise<void> => {
    try {
      await umzugApi.importVerwerfen(a.id);
      onNeu();
      onFortsetzen();
    } catch (err) {
      onFehler(errorText(err));
    }
  };

  const idx = schrittIndex(a);
  const ergebnis = a.status === "fertig" ? (a.ergebnis as UmzugImportErgebnis | null) : null;

  return (
    <Card
      title={ergebnis ? `„${ergebnis.name}“ importiert` : a.optionen?.dateiname ?? "Import"}
      actions={<AuftragStatus a={a} />}
      className="mb-5"
    >
      <ol className="mb-4 grid gap-2 sm:grid-cols-5">
        {SCHRITTE.map((s, i) => {
          const fertig = i < idx;
          const jetzt = i === idx && laeuftNoch(a);
          const kaputt = i === idx && (a.status === "fehler" || a.status === "kuerzel_noetig");
          return (
            <li key={s.key} className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-xs ${kaputt ? "border-red-500/40 text-red-700 dark:text-red-300" : fertig ? "border-default text-primary" : jetzt ? "border-brand-primary/50 text-primary" : "border-default text-muted"}`}>
              <span className={`inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${kaputt ? "bg-red-500/15" : fertig ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : jetzt ? "bg-brand-primary/15" : "bg-input"}`}>
                {kaputt ? <Sym name="x" className="h-3 w-3" /> : fertig ? <Sym name="check" className="h-3 w-3" /> : <span className="text-[0.65rem] font-semibold">{i + 1}</span>}
              </span>
              <span className="min-w-0">{s.label}</span>
            </li>
          );
        })}
      </ol>

      {laeuftNoch(a) && <p className="text-sm text-secondary">{fortschrittText(a)}</p>}

      {a.status === "kuerzel_noetig" && (
        <Callout tone="warning" title="Anderes Kürzel nötig">
          <p>{a.fehler}</p>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-end">
            <label className="block sm:w-64">
              <span className="field-label">Neues Kürzel</span>
              <input className="input font-mono" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="z. B. radio-eifel-2" spellCheck={false} autoComplete="off" />
            </label>
            <Button variant="primary" disabled={laeuft || slug.trim().length < 2} onClick={() => void fortsetzen()}>Fortsetzen</Button>
            <Button variant="ghost" onClick={() => void verwerfen()}>Verwerfen</Button>
          </div>
          <p className="mt-2 text-xs">Das Archiv liegt noch auf dem Server – es muss nicht neu hochgeladen werden (24 Stunden lang).</p>
        </Callout>
      )}

      {a.status === "fehler" && (
        <Callout tone="danger" title="Import abgebrochen – es wurde nichts angelegt">
          <p>{a.fehler}</p>
          <FehlerDetails a={a} />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="ghost" onClick={() => void verwerfen()}><Sym name="trash" /> Archiv verwerfen</Button>
            <Button variant="ghost" onClick={onNeu}>Anderes Archiv</Button>
          </div>
        </Callout>
      )}

      {ergebnis && (
        <>
          <Callout tone="warning" title="Das Radio ist gesperrt" className="mb-4">
            Damit nicht zwei Server gleichzeitig dasselbe Radio senden: erst den alten Server abschalten, dann hier prüfen und entsperren.
          </Callout>
          <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Zahl label="Kürzel" wert={ergebnis.slug} mono />
            <Zahl label="Datensätze" wert={zahlText(Object.values(ergebnis.zeilen).reduce((s, n) => s + n, 0))} />
            <Zahl label="Tabellen" wert={zahlText(Object.keys(ergebnis.zeilen).length)} />
            <Zahl label="Dateien" wert={`${zahlText(ergebnis.dateien)} · ${bytesText(ergebnis.bytes)}`} />
          </div>
          {ergebnis.hinweise.length > 0 && (
            <div className="mb-4">
              <div className="section-label mb-2">Hinweise</div>
              <ul className="space-y-1.5 text-sm text-secondary">
                {ergebnis.hinweise.map((h) => <li key={h} className="flex gap-2"><Sym name="alert" className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />{h}</li>)}
              </ul>
            </div>
          )}
          <details className="mb-4 text-sm">
            <summary className="cursor-pointer text-secondary">Zeilen je Tabelle</summary>
            <ul className="mt-2 grid gap-x-6 gap-y-1 sm:grid-cols-2">
              {Object.entries(ergebnis.zeilen).sort((x, y) => y[1] - x[1]).map(([t, n]) => (
                <li key={t} className="flex justify-between gap-3 font-mono text-xs"><span className="truncate">{t}</span><span className="tabular-nums">{zahlText(n)}</span></li>
              ))}
            </ul>
          </details>
          <div className="flex flex-wrap gap-2">
            <Link to={`/betrieb/radios/${ergebnis.tenant_id}`} className="btn btn-primary">Zum Radio</Link>
            <Button variant="ghost" onClick={onNeu}>Weiteres Radio importieren</Button>
          </div>
        </>
      )}
    </Card>
  );
}

function FehlerDetails({ a }: { a: UmzugAuftrag }): JSX.Element | null {
  const d = a.fehler_details;
  if (!d) return null;
  return (
    <div className="mt-2 space-y-2 text-sm">
      {d.kollisionen && d.kollisionen.length > 0 && (
        <ul className="space-y-1">
          {d.kollisionen.map((k) => (
            <li key={k.tabelle}><code className="font-mono">{k.tabelle}</code>: {zahlText(k.anzahl)} schon vorhanden <span className="text-xs opacity-80">(z. B. {k.beispiele.slice(0, 2).join(", ")})</span></li>
          ))}
        </ul>
      )}
      {d.emails && d.emails.length > 0 && (
        <ul className="space-y-1">
          {d.emails.map((e) => <li key={e.email}><b>{e.email}</b> – gehört hier zu „{e.radio}“</li>)}
        </ul>
      )}
      {d.abweichungen && d.abweichungen.length > 0 && (
        <ul className="list-inside list-disc font-mono text-xs">{d.abweichungen.slice(0, 10).map((x) => <li key={x}>{x}</li>)}</ul>
      )}
    </div>
  );
}

function Zahl({ label, wert, mono = false }: { label: string; wert: string; mono?: boolean }): JSX.Element {
  return (
    <div className="rounded-lg border border-default p-3">
      <div className="text-[0.75rem] font-medium text-muted">{label}</div>
      <div className={`mt-1 truncate text-base font-semibold tabular-nums text-primary ${mono ? "font-mono" : ""}`}>{wert}</div>
    </div>
  );
}

